import { useEffect, useMemo, useState } from 'react';
import { type MRT_ColumnDef } from 'material-react-table';
import DataTable from '@/components/data-table/DataTable';
import FuseLoading from '@fuse/core/FuseLoading';
import { Chip, IconButton, Paper, Tooltip } from '@mui/material';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import Link from '@fuse/core/Link';
import Typography from '@mui/material/Typography';
import ConfirmationDialog from '@/pages/apps/users/Dialog/ConfirmationDialog';
import { toast } from 'react-toastify';
import { useDeleteAreaMutation, useGetAreasQuery } from '@/store/services/areas';
import ApplicationsStatus from "@/pages/apps/applications/ApplicationsStatus";

function UsersTable() {
	const [search, setSearch] = useState('');
	const [query, setQuery] = useState('');
	const [selectedArea, setSelectedArea] = useState<{ id: string; name: string } | null>(null);
	const [pagination, setPagination] = useState({
		pageIndex: 0,
		pageSize: 10
	});

	const { data: areasData = [], isLoading, isFetching } = useGetAreasQuery({
		search: query,
		page: pagination.pageIndex + 1,
		per_page: pagination.pageSize,
		pagination: true
	}, {
		skip: pagination.pageIndex === null || pagination.pageSize === null,
		refetchOnMountOrArgChange: true
	});

	const showTableLoading = isFetching && !isLoading;
	
	const [deleteArea, { isLoading: isDeleting }] = useDeleteAreaMutation();

	useEffect(() => {
		const delayDebounceFn = setTimeout(() => {
			setQuery(search);
		}, 500);

		return () => clearTimeout(delayDebounceFn);
	}, [search]);

	const handleDelete = (id: string | number, name: string) => {
		setSelectedArea({ id: String(id), name });
	};

	const handleDeleteConfirmation = async () => {
		if (!selectedArea) return;

		const response: any = await deleteArea(selectedArea.id);

		if (!response.error) {
			toast.success('Area deleted successfully');
		} else {
			toast.error(response?.error?.data?.errors?.[0]?.title || 'Something went wrong');
		}

		setSelectedArea(null);
	};

	const columns = useMemo<MRT_ColumnDef<any>[]>(
		() => [
			{
				accessorKey: 'name',
				header: 'Name',
				enableColumnFilter: false,
        		enableColumnDragging: false,
				enableColumnActions: false,
				Cell: ({ row }) => (
					<Typography
						component={Link}
						to={`/apps/areas/${row.original.id}`}
						role="button"
					>
						<u>{row.original.name}</u>
					</Typography>
				)
			},
			{
				accessorKey: 'status',
				header: 'Status',
				enableColumnFilter: false,
        		enableColumnDragging: false,
				enableColumnActions: false,
				Cell: ({ row }) => <ApplicationsStatus name={row.original.status===1 ? 'Active' : 'Inactive'} />
			},
			{
				accessorKey: 'actions',
				header: 'Actions',
				enableColumnFilter: false,
				enableColumnDragging: false,
				enableSorting: false,
				enableColumnActions: false,
				size: 90,
				maxSize: 90,
				Cell: ({ row }) => (
					<div className="flex items-center gap-1">
						<Tooltip title="Edit Area">
							<IconButton
								size="small"
								component={Link}
								to={`/apps/areas/${row.original.id}`}
								aria-label="edit-area"
							>
								<FuseSvgIcon>heroicons-outline:pencil-square</FuseSvgIcon>
							</IconButton>
						</Tooltip>
						<Tooltip title="Delete Area">
							<IconButton
								size="small"
								onClick={(event) => {
									event.stopPropagation();
									handleDelete(row?.original?.id, row?.original?.name);
								}}
								aria-label="delete-area"
							>
								<FuseSvgIcon>heroicons-outline:trash</FuseSvgIcon>
							</IconButton>
						</Tooltip>
					</div>
				)
			},
		],
		[]
	);

	if (isLoading) {
		return <FuseLoading />;
	}

	return (
		<Paper
			className="flex flex-col flex-auto shadow-1 rounded-lg overflow-hidden w-full h-full shadow-sm"
			elevation={0}
		>
			<ConfirmationDialog
				open={!!selectedArea}
				onClose={() => setSelectedArea(null)}
				onConfirm={handleDeleteConfirmation}
				title="Delete Area"
				message="Are you sure you want to delete the area"
				itemName={selectedArea?.name}
				isLoading={isDeleting}
			/>
			<div className='h-[calc(100vh-147px)]'>
				<DataTable
					data={areasData?.data?.data || []}
					columns={columns}
					emptyMessage="No areas found. Add your first area using the button above."
					query={search}
					setQuery={setSearch}
					enableDensityToggle={false}
					enableSorting={false}
					enableHiding={false}
					enableToolbarInternalActions={false}
					state={{
						pagination,
						columnPinning: { left: ['mrt-row-expand'], right: ['actions'] },
						isLoading: showTableLoading,
						showLoadingOverlay: showTableLoading,
						showSkeletons: showTableLoading,
					}}
					rowCount={areasData?.data?.total}
					manualPagination
					onPaginationChange={setPagination}
					enableRowSelection={false}
					enableRowActions={false}
					enableColumnFilters={false}
					searchPlaceholder="Search by name"
					enableExpanding
					getRowCanExpand={(row) => row?.original?.line_items?.length}
					renderDetailPanel={({ row }) => (
						<div className="px-4 bg-gray-100 rounded-lg flex items-center gap-4">
							<Typography variant="h6" className='text-lg font-semibold whitespace-nowrap'>
								Line Items:
							</Typography>
							<div className='flex flex-wrap items-center gap-4'>
							{row?.original?.line_items?.map((item: { name: string; id: number; is_used?: boolean }, index: number) => {
								const chip = (
									<Chip
										label={item?.name}
										disabled={item?.is_used === true}
									/>
								);

								if (!item?.is_used) {
									return (
										<div key={item?.id ?? index} className="flex justify-between">
											{chip}
										</div>
									);
								}

								return (
									<Tooltip
										key={item?.id ?? index}
										title="This line item is used in an application so it can't be removed."
										arrow
									>
										<span>{chip}</span>
									</Tooltip>
								);
							})}
						</div>
					</div>
				)}
			/>
			</div>
		</Paper>
	);
}

export default UsersTable;
